import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import SearchIcon from '../assets/images/icons/SearchIcon';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../store/store';
import {UserService} from '../services/userService';
import {SearchType} from '../types/search';
import {User} from '../types/user';
import {setActiveTab} from '../features/user/userSlice';
import {FaUser} from 'react-icons/fa';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({isOpen, onClose}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setUsers([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 400);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const performSearch = async (query: string) => {
    if (query.trim() !== '') {
      setIsLoading(true);
      const search: SearchType<{
        [key: string]: {operator: string; value: string};
      }> = {
        where: {
          name: {
            operator: 'like',
            value: query,
          },
          hidden: {
            operator: '==',
            value: 'false',
          },
        },
      };

      try {
        const response = await UserService.index(1, search, 'name', 'asc', 10);
        setUsers(response.data);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsLoading(false);
      }
    } else if (query.trim() === '') {
      setUsers([]);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 400);
  };

  const handleUserClick = (userId: string) => {
    navigate(`/hr/users/${userId}`);
    dispatch(setActiveTab('HR Documents'));
    onClose();
  };

  const handleClose = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery('');
    setUsers([]);
    onClose();
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center">
      <div ref={modalRef} className="bg-white rounded-lg w-full max-w-2xl mx-4">
        <div className="p-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400 ">
              <SearchIcon />
            </div>
            <input
              ref={inputRef}
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-cyan-400 focus:border-cyan-400 sm:text-sm"
              aria-label="Search users"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            </div>
          ) : (
            <>
              {users.length === 0 && searchQuery.trim() !== '' ? (
                <div className="py-6 px-4 text-center text-gray-500">
                  No users found for "{searchQuery}"
                </div>
              ) : (
                <ul>
                  {users.map((user: User) => (
                    <li
                      key={user.id}
                      onClick={() => handleUserClick(user.id)}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ease-in-out">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-full bg-gray-100 text-slate-400 flex items-center justify-center">
                          {user?.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt="Profile"
                              className="w-full h-full  rounded-full object-cover"
                            />
                          ) : (
                            <FaUser className="text-gray-400 size-6" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right sm:px-6 rounded-b-lg">
          <button
            type="button"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-500 hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
